import { IconButton, Link, Stack, TooltipHost } from "@fluentui/react";
import { styles } from "./experimentsTable.styles";
import { ExperimentType, PipelineStatus, PipelineStatusMap } from "../Types";
import { getExpTreatment, normalizeEv2Status } from "../Shared/shared.utils";
import {
  Edit20Regular,
  Folder16Regular,
  FolderOpen16Regular,
  Star20Regular,
} from "@fluentui/react-icons";
import {
  notSetCell,
  accessibleTextRenderer,
  loadingRenderer,
} from "../Shared/TableRenderers";
import ExperimentLink from "../Shared/LinkComponents/ExperimentLink";
import {
  ARCHIVE_KEY,
  LONG_RUNNING_THRESHOLD_DAYS,
  UNARCHIVE_KEY,
} from "../Shared/shared.consts";
import { baseTrackNavigationEvent, NavType } from "../Shared/AppInsights";
import { statusRenderer } from "../Experiment/RolloutDetails/RolloutDetailsTable/rolloutDetailsColumnRenderers";
import upperFirst from "lodash/upperFirst";
import { ROLLBACK_TYPE } from "../Experiment/RolloutDetails/RolloutDetailsTable/RolloutDetailsEv2Panel/ev2TreatmentStages.consts";

export const renderFeatureNameAndDescription = (item: ExperimentType) => {
  if (!item.featureName) {
    return notSetCell("Feature name not set");
  }

  return (
    <div className={styles.experimentTableContainer}>
      <div className={styles.experimentTableTitleContainer}>
        <Link as="div">
          <ExperimentLink
            extension={item.__extension}
            experiment={item.featureKey}
            className={styles.experimentTableTitle}
          >
            {accessibleTextRenderer(item.featureName)}
          </ExperimentLink>
        </Link>
        {item.prUrl && item.pr?.title?.includes(ARCHIVE_KEY) ? (
          <span className={styles.experimentTablePrDeleteBadge}>
            <Folder16Regular className={styles.experimentTableIcon} />
            {accessibleTextRenderer("Archive in progress. ")}
            <Link
              href={item.prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.experimentTablePrDeleteBadgeLink}
              onClick={() => {
                baseTrackNavigationEvent({
                  component: "ExperimentsTable",
                  extensionName: item.__extension,
                  experimentName: item.featureKey,
                  function: "renderFeatureNameAndDescription",
                  destination: item.prUrl!,
                  navType: NavType.External,
                });
              }}
            >
              {accessibleTextRenderer("Click here to review")}
            </Link>
          </span>
        ) : item.prUrl && item.pr?.title?.includes(UNARCHIVE_KEY) ? (
          <span className={styles.experimentTablePrBadge}>
            <FolderOpen16Regular className={styles.experimentTableIcon} />
            {accessibleTextRenderer("Unarchive in progress. ")}
            <Link
              href={item.prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.experimentTablePrBadgeLink}
              onClick={() => {
                baseTrackNavigationEvent({
                  component: "ExperimentsTable",
                  extensionName: item.__extension,
                  experimentName: item.featureKey,
                  destination: item.prUrl!,
                  function: "renderFeatureNameAndDescription",
                  navType: NavType.External,
                });
              }}
            >
              {accessibleTextRenderer("Click here to review")}
            </Link>
          </span>
        ) : (
          <>
            {item.isNew && (
              <span className={styles.experimentTableNewBadge}>
                <Star20Regular className={styles.experimentTableIcon} />
                {accessibleTextRenderer("New experiment. ")}
                <Link
                  href={item.prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.experimentTableNewBadgeLink}
                  onClick={() => {
                    baseTrackNavigationEvent({
                      component: "ExperimentsTable",
                      extensionName: item.__extension,
                      experimentName: item.featureKey,
                      destination: item.prUrl!,
                      function: "renderFeatureNameAndDescription",
                      navType: NavType.External,
                    });
                  }}
                >
                  {accessibleTextRenderer("Click here to review")}
                </Link>
              </span>
            )}
            {item.prUrl && !item.isNew && (
              <span className={styles.experimentTablePrBadge}>
                <Edit20Regular className={styles.experimentTableIcon} />
                {accessibleTextRenderer("Config update in progress. ")}
                <Link
                  href={item.prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.experimentTablePrBadgeLink}
                  onClick={() => {
                    baseTrackNavigationEvent({
                      component: "ExperimentsTable",
                      extensionName: item.__extension,
                      experimentName: item.featureKey,
                      destination: item.prUrl!,
                      function: "renderFeatureNameAndDescription",
                      navType: NavType.External,
                    });
                  }}
                >
                  {accessibleTextRenderer("Click here to review")}
                </Link>
              </span>
            )}
          </>
        )}
      </div>
      {item.featureDescription && (
        <div className={styles.experimentTableDescription}>
          {accessibleTextRenderer(item.featureDescription)}
        </div>
      )}
    </div>
  );
};

export const renderLastModifiedBy = (item: ExperimentType) => {
  if (!item.lastModifiedBy) {
    return notSetCell("Unknown");
  }
  return <span>{accessibleTextRenderer(item.lastModifiedBy)}</span>;
};

export const renderLastModifiedDate = (item: ExperimentType) => {
  if (!item.lastModifiedDate) {
    return notSetCell("Unknown");
  }

  // Convert to local timezone and format the date
  const date = new Date(item.lastModifiedDate);
  const localDateString = date.toLocaleString();

  return <span>{accessibleTextRenderer(localDateString)}</span>;
};

export const renderEnvironment = (item: ExperimentType) => {
  if (!item.environment) {
    return notSetCell("Environment not set");
  }
  return <span>{accessibleTextRenderer(item.environment)}</span>;
};

const STATUS_TOOLTIPS: Partial<Record<PipelineStatus, string>> = {
  [PipelineStatus.COMPLETED]:
    "Fully advanced — this experiment is running at its final treatment stage.",
  [PipelineStatus.RUNNING]:
    "Still advancing — this experiment has not yet reached its final treatment stage.",
  [PipelineStatus.FAILED]:
    "This rollout has failed. Please open experiment for more details.",
  [PipelineStatus.WARNING]:
    "Health check failed - please review the rollout for additional details.",
};

export const renderRolloutStatus = ({
  item,
  environment,
  runPipelineStatuses,
  loadingPipelineStatuses,
}: {
  item: ExperimentType;
  environment: string;
  runPipelineStatuses: PipelineStatusMap;
  loadingPipelineStatuses: boolean;
}) => {
  const configFileName = item.__filename;
  const { statuses, daysSinceStart = null } =
    runPipelineStatuses[configFileName]?.[environment] || {};
  const loading = loadingPipelineStatuses && !statuses;

  if (loading) {
    return loadingRenderer("Loading status...");
  }
  if (!statuses || statuses.length === 0) {
    return notSetCell("No rollouts");
  }

  // Get the latest status
  const latestStatus = statuses[0];
  const abnVariantCount = item.variantDetails?.length;
  const normalizedStatus = normalizeEv2Status(latestStatus);

  const treatment = getExpTreatment(
    latestStatus.stageName,
    true,
    abnVariantCount,
  );
  const statusText = `${upperFirst(normalizedStatus)} (${treatment})`;

  // Calculate how long the rollout has been running based on the earliest status start time
  const earliestStatus = statuses[statuses.length - 1];
  const startDate = new Date(earliestStatus.startTime);
  const readableDaysText =
    daysSinceStart === null
      ? ""
      : daysSinceStart < 1
        ? "Less than a day"
        : `${daysSinceStart} day${daysSinceStart !== 1 ? "s" : ""}`;
  const isLongRunning =
    daysSinceStart && daysSinceStart >= LONG_RUNNING_THRESHOLD_DAYS;

  const durationElement = (
    <span title={`Started ${readableDaysText} ago`}>
      Started{" "}
      <TooltipHost content={startDate.toLocaleString()}>
        <span className={styles.experimentTableDurationText}>
          {readableDaysText}
        </span>
      </TooltipHost>{" "}
      ago
    </span>
  );

  return (
    <Stack>
      {statusRenderer(
        statusText,
        normalizedStatus,
        loading,
        undefined,
        undefined,
        STATUS_TOOLTIPS[normalizedStatus],
      )}
      {treatment !== ROLLBACK_TYPE &&
        (isLongRunning
          ? statusRenderer(
              durationElement,
              PipelineStatus.WARNING,
              false,
              undefined,
              undefined,
              `This rollout has been running for over ${LONG_RUNNING_THRESHOLD_DAYS} days. Please review the rollout to ensure it's still active and necessary.`,
            )
          : durationElement)}
    </Stack>
  );
};

export const renderArchiveAction = (
  item: ExperimentType,
  handleArchive: (item: ExperimentType) => void,
  hasAdoAccess?: boolean | undefined,
) => {
  return (
    <IconButton
      iconProps={{ iconName: "FabricFolder" }}
      title="Archive Experiment"
      ariaLabel="Archive Experiment"
      onClick={() => handleArchive(item)}
      disabled={item?.pr?.title?.includes(ARCHIVE_KEY) || !hasAdoAccess}
      style={{
        background: "unset",
      }}
    />
  );
};

export const renderActions = (
  item: ExperimentType,
  handleClone: (item: ExperimentType) => void,
  hasAdoAccess?: boolean | undefined,
) => {
  return (
    <IconButton
      iconProps={{ iconName: "Copy" }}
      title="Clone Experiment"
      ariaLabel="Clone Experiment"
      onClick={() => handleClone(item)}
      disabled={!hasAdoAccess}
    />
  );
};
